import type { Palette } from '@mui/material/styles';
import {
    BannerDismissals,
    BannerViewer,
    DynamicBanner,
} from './bannerTypes';

// -------------------------------------------------------
// 1. Default banner colors
// -------------------------------------------------------

// Banner default colors are supplied by the existing theme palette.

// -------------------------------------------------------
// 2. Validate configured colors
// -------------------------------------------------------

const isValidHexColor = (
    value: unknown
): value is string => {
    return (
        typeof value === 'string' &&
        /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(value)
    );
};

// -------------------------------------------------------
// 3. Calculate color contrast
// -------------------------------------------------------

const getLuminance = (hexColor: string): number => {
    const raw = hexColor.replace('#', '');

    const normalized =
        raw.length === 3
            ? raw
                .split('')
                .map((character) => character + character)
                .join('')
            : raw;

    const channels = [0, 2, 4].map((index) => {
        const value =
            Number.parseInt(normalized.slice(index, index + 2), 16) /
            255;

        return value <= 0.04045
            ? value / 12.92
            : Math.pow((value + 0.055) / 1.055, 2.4);
    });

    return (
        channels[0] * 0.2126 +
        channels[1] * 0.7152 +
        channels[2] * 0.0722
    );
};

const getContrastRatio = (
    firstColor: string,
    secondColor: string
): number => {
    const first = getLuminance(firstColor);
    const second = getLuminance(secondColor);

    return (
        (Math.max(first, second) + 0.05) /
        (Math.min(first, second) + 0.05)
    );
};

// -------------------------------------------------------
// 4. Get banner styling with defaults
// -------------------------------------------------------

export const getBannerColors = (
    banner: DynamicBanner,
    colors: Palette
) => {
    const defaults =
        colors.bannerColors?.[banner.type] ||
        colors.bannerColors?.info;

    const styling = banner.styling;

    const backgroundColor = styling?.backgroundColor;

    const defaultBackground = defaults?.background ?? colors.background.paper;
    const defaultText = defaults?.text ?? colors.common.black;
    const defaultAccent = defaults?.accent ?? colors.divider;

    const background = isValidHexColor(backgroundColor)
        ? backgroundColor
        : defaultBackground;

    // Choose a readable fallback when the configured
    const fallbackText = '#000000';

    const configuredText = styling?.fontColor;
    let text: string;
    if (isValidHexColor(configuredText)) {
        text = configuredText;
    } else if (getContrastRatio(defaultText, background) >= 4.5) {
        text = defaultText;
    } else {
        text = fallbackText;
    }

    const titleColor = styling?.titleColor;

    const title = isValidHexColor(titleColor)
        ? titleColor
        : text;

    const fontSizeValue = styling?.fontSize;
    const configuredFontSize = typeof fontSizeValue === 'string' &&
        /^\d+(?:\.\d+)?px$/.test(fontSizeValue.trim())
        ? Number.parseFloat(fontSizeValue)
        : Number.NaN;
    const fontSize = Number.isFinite(configuredFontSize)
        ? Math.min(20, Math.max(12, configuredFontSize))
        : 14;

    return {
        background,
        text,
        title,
        fontSize,
        accent: isValidHexColor(styling?.borderColor)
            ? styling?.borderColor
            : defaultAccent,
    };
};

// Per-banner revision is preferred; the wrapper's updatedAt
// is a fallback until the backend sends individual revisions.
export const getBannerRevision = (
    banner: DynamicBanner,
    dashboardUpdatedAt?: string
): string => String(
    banner.revision ?? banner.updatedAt ?? dashboardUpdatedAt ?? '1'
);

// -------------------------------------------------------
// 5. Match banner with current page
// -------------------------------------------------------

export const matchesBannerPath = (
    pattern: string,
    pathname: string
): boolean => {
    const normalizePath = (path: string): string => {
        if (path === '/') return '/';

        let end = path.length;
        while (end > 0 && path[end - 1] === '/') {
            end -= 1;
        }
        return path.slice(0, end);
    };

    const currentPath = normalizePath(pathname);
    const configuredPath = normalizePath(pattern);

    // Global wildcard
    if (configuredPath === '*') {
        return true;
    }

    // Prefix matching
    // Example: /dataOps/*
    if (configuredPath.endsWith('/*')) {
        const prefix = normalizePath(
            configuredPath.slice(0, -2)
        );

        if (prefix === '/') return true;

        return (
            currentPath === prefix ||
            currentPath.startsWith(`${prefix}/`)
        );
    }

    // Exact page matching
    return currentPath === configuredPath;
};

// -------------------------------------------------------
// 6. User segment targeting
// -------------------------------------------------------

export const matchesBannerTarget = (
    banner: DynamicBanner,
    viewer: BannerViewer
): boolean => {
    const targeting = banner.targetSegments;

    // No targeting configured: visible to all users
    // authorized to receive the banner.
    if (!targeting) {
        return true;
    }

    const matchesValue = (
        allowedValues: string[] | undefined,
        actualValue?: string
    ): boolean => {
        if (!allowedValues?.length ||
            allowedValues.some((value) => value.trim().toLowerCase() === 'all')) {
            return true;
        }

        if (!actualValue) {
            return false;
        }

        return allowedValues.some(
            (value) =>
                value.trim().toLowerCase() ===
                actualValue.trim().toLowerCase()
        );
    };

    // User may have multiple capabilities.
    // Backend targetSegments.roles lists capabilities, not JWT roles.
    const roleMatches =
        !targeting.roles?.length ||
        targeting.roles.some(
            (role) => role.trim().toLowerCase() === 'all'
        ) ||
        viewer.capabilities.some((capability) =>
            matchesValue(targeting.roles, capability)
        );

    const planMatches = matchesValue(
        targeting.plans,
        viewer.plan
    );

    const regionMatches = matchesValue(
        targeting.regions,
        viewer.region
    );

    // All configured targeting dimensions must match.
    return (
        roleMatches &&
        planMatches &&
        regionMatches
    );
};

// -------------------------------------------------------
// 7. Parse timezone-aware date/time
// -------------------------------------------------------

const parseBannerDate = (
    dateString?: string | null
): number | null => {
    if (!dateString) {
        return null;
    }

    // Require an explicit timezone, e.g.:
    // 2026-10-19T10:00:00Z
    // 2026-10-19T15:30:00+05:30
    const hasTimezone =
        /(Z|[+-]\d{2}:\d{2})$/i.test(dateString);

    if (!hasTimezone) {
        return null;
    }

    const timestamp = Date.parse(dateString);

    return Number.isFinite(timestamp)
        ? timestamp
        : null;
};

// -------------------------------------------------------
// 8. Automatic start/end scheduling
// -------------------------------------------------------

export const isBannerScheduled = (
    banner: DynamicBanner,
    now: number = Date.now()
): boolean => {
    const start = banner.scheduling?.startTime
        ? parseBannerDate(banner.scheduling.startTime)
        : -Infinity;

    const end = banner.scheduling?.endTime
        ? parseBannerDate(banner.scheduling.endTime)
        : Infinity;

    // Reject invalid configured timestamps.
    if (start === null || end === null) {
        return false;
    }

    // Start is inclusive; end is exclusive.
    return start <= now && now < end;
};

// -------------------------------------------------------
// 9. Sort banners by priority and display order
// -------------------------------------------------------

export const sortBannersByPriority = (
    banners: DynamicBanner[]
): DynamicBanner[] => {
    return [...banners].sort((first, second) => {
        const priorityDifference =
            (first.priority ?? 0) -
            (second.priority ?? 0);

        if (priorityDifference !== 0) {
            return priorityDifference;
        }

        const displayOrderDifference =
            (first.displayOrder ?? 0) -
            (second.displayOrder ?? 0);

        if (displayOrderDifference !== 0) {
            return displayOrderDifference;
        }

        // Consistent ordering when values are equal.
        return first.bannerId.localeCompare(second.bannerId);
    });
};

// -------------------------------------------------------
// 10. Check for non-empty banner content
// -------------------------------------------------------

const stripHtmlTags = (message: string): string => {
    let result = '';
    let contentStart = 0;
    let tagStart = -1;

    for (let index = 0; index < message.length; index += 1) {
        if (message[index] === '<' && tagStart === -1) {
            tagStart = index;
        } else if (message[index] === '>' && tagStart !== -1) {
            result += message.slice(contentStart, tagStart);
            contentStart = index + 1;
            tagStart = -1;
        }
    }

    return result + message.slice(contentStart);
};

export const hasBannerContent = (
    banner: DynamicBanner
): boolean => {
    const hasTitle = Boolean(banner.bannerTitle?.trim());

    const message = banner.bannerText?.trim() || '';

    // This is a basic emptiness check, not an HTML sanitizer.
    // The UI component must sanitize HTML before rendering.
    const plainMessage = stripHtmlTags(message)
        .replace(/&nbsp;|&#160;/gi, ' ')
        .trim();

    return hasTitle || Boolean(plainMessage);
};

// -------------------------------------------------------
// 11. LocalStorage key scoped by user
// -------------------------------------------------------

export const getBannerStorageKey = (
    userId: string
): string => {
    return (
        'pepwise:banners:dismissed:v1:' +
        encodeURIComponent(userId)
    );
};

// -------------------------------------------------------
// 12. Read dismissed banners from localStorage
// -------------------------------------------------------

export const readDismissals = (
    userId: string
): BannerDismissals => {
    if (!userId || typeof window === 'undefined') {
        return {};
    }

    try {
        const storedData = window.localStorage.getItem(
            getBannerStorageKey(userId)
        );

        if (!storedData) {
            return {};
        }

        const parsed: unknown = JSON.parse(storedData);

        if (
            !parsed ||
            typeof parsed !== 'object' ||
            Array.isArray(parsed)
        ) {
            return {};
        }

        const result: BannerDismissals = Object.create(null);

        Object.entries(parsed).forEach(([bannerId, value]) => {
            if (
                !value ||
                typeof value !== 'object' ||
                Array.isArray(value)
            ) {
                return;
            }

            const entry = value as Record<string, unknown>;

            if (
                entry.viewed !== 1 ||
                (
                    typeof entry.revision !== 'string' &&
                    typeof entry.revision !== 'number'
                )
            ) {
                return;
            }

            result[bannerId] = {
                viewed: 1,
                revision: String(entry.revision),
                dismissedAt:
                    typeof entry.dismissedAt === 'string'
                        ? entry.dismissedAt
                        : '',
            };
        });

        return result;
    } catch (error) {
        console.warn(
            'Unable to read PepWise banner dismissal state:',
            error
        );

        return {};
    }
};

// -------------------------------------------------------
// 13. Save dismissed banners in localStorage
// -------------------------------------------------------

export const saveDismissals = (
    userId: string,
    dismissals: BannerDismissals
): void => {
    if (!userId || typeof window === 'undefined') {
        return;
    }

    try {
        window.localStorage.setItem(
            getBannerStorageKey(userId),
            JSON.stringify(dismissals)
        );
    } catch (error) {
        console.warn(
            'Unable to save PepWise banner dismissal state:',
            error
        );
    }
};

// -------------------------------------------------------
// 14. Validate CTA / hyperlink URLs
// -------------------------------------------------------

export const safeBannerUrl = (
    rawUrl?: string
): string | null => {
    if (!rawUrl || typeof window === 'undefined') {
        return null;
    }

    const value = rawUrl.trim();

    if (
        !value ||
        value.startsWith('//') ||
        value.includes('\\') ||
        /[\u0000-\u001F\u007F]/.test(value)
    ) {
        return null;
    }

    try {
        const parsedUrl = new URL(
            value,
            window.location.origin
        );

        // Only HTTP and HTTPS links are allowed.
        if (
            parsedUrl.protocol !== 'http:' &&
            parsedUrl.protocol !== 'https:'
        ) {
            return null;
        }

        // Reject URLs containing embedded credentials.
        if (parsedUrl.username || parsedUrl.password) {
            return null;
        }

        return parsedUrl.href;
    } catch {
        return null;
    }
};

// -------------------------------------------------------
// 15. Extract user details from existing PepWise JWT
// -------------------------------------------------------

export const getBannerViewerFromToken = (
    token: unknown,
    capabilities: unknown = []
): BannerViewer | null => {
    if (
        !token ||
        typeof token !== 'object' ||
        Array.isArray(token)
    ) {
        return null;
    }

    const claims = token as Record<string, unknown>;

    const asString = (value: unknown): string | undefined => {
        return typeof value === 'string' && value.trim()
            ? value.trim()
            : undefined;
    };

    // PepWise auth may use sub, userId, id or email.
    const userId =
        asString(claims.sub) ||
        asString(claims.userId) ||
        asString(claims.id) ||
        asString(claims.email);

    if (!userId) {
        return null;
    }

    // Capabilities come from PepWiseAuthController, not JWT roles.
    // Support arrays of strings (or named capabilities), and
    // boolean capability maps if supplied by the auth context.
    let capabilityItems: unknown[] = [];
    if (Array.isArray(capabilities)) {
        capabilityItems = capabilities;
    } else if (capabilities && typeof capabilities === 'object') {
        capabilityItems = Object.entries(capabilities)
            .filter(([, enabled]) => enabled === true)
            .map(([name]) => name);
    }

    const normalizedCapabilities = capabilityItems
        .map((capability): string => {
            if (typeof capability === 'string') {
                return capability.trim();
            }

            if (
                capability &&
                typeof capability === 'object' &&
                !Array.isArray(capability)
            ) {
                const entry = capability as Record<string, unknown>;
                return asString(entry.name) || '';
            }

            return '';
        })
        .filter(Boolean);

    return {
        id: userId,
        email: asString(claims.email),
        capabilities: Array.from(new Set(normalizedCapabilities)),
        plan: asString(claims.plan),
        region: asString(claims.region),
    };
};
